import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import StaffSelect from '@/components/select/StaffSelect';
import StaffEnrollmentWarning from '@/components/StaffEnrollmentWarning/StaffEnrollmentWarning';
import useStaff from '@/hooks/useStaff';
import { useStaffEnrollment } from '@/hooks/useStaffEnrollment';
import { useStaffEnrollments } from '@/hooks/useStaffEnrollments';
import InsuranceRepository from '@/repositories/InsuranceRepository';
import JurisdictionRepository from '@/repositories/JurisdictionRepository';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Container from '@/ui/components/Container';
import Checkbox from '@/ui/components/Form/Checkbox';
import { OptionType } from '@/ui/components/Form/Select/types';
import setStaffSelectValue from '@/utils/formatStaffAsSelectOption';

const PROGRAM_SCOPES = [
  { label: 'Substance Use Disorder', value: 'substance_use_disorder' },
  { label: 'Mental Health', value: 'mental_health' }
];

const formatSeperatedString = (str: string | null | undefined, sep: string = ',') => {
  // Format a string as a ul / li list
  const list = str
    ? str
        .split(sep)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    : [];
  if (list.length === 0) return '-';
  return (
    <ul className="list-disc list-inside">
      {list.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
};

const Credentialing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStaff, setSelectedStaff] = useState<OptionType<string> | null>(null);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<OptionType<string> | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<OptionType<string> | null>(null);
  const [selectedType, setSelectedType] = useState<OptionType<string> | null>(null);
  const [selectedProgramScope, setSelectedProgramScope] = useState<OptionType<string> | null>(null);
  const [onlyActive, setOnlyActive] = useState<boolean>(true);

  const insuranceRepository = InsuranceRepository();
  const jurisdictionRepository = JurisdictionRepository();

  const { data: staffData } = useStaff({ withKustomerId: true });

  const { data: jurisdictions } = useQuery({
    queryKey: ['jurisdictions'],
    queryFn: () => jurisdictionRepository.getJurisdictions()
  });

  const { data: providers } = useQuery({
    queryKey: ['insuranceProviders'],
    queryFn: () => insuranceRepository.getInsuranceProviders()
  });

  const { data: types } = useQuery({
    queryKey: ['insuranceTypes'],
    queryFn: () => insuranceRepository.getPlanTypes()
  });

  // Sync URL params to state
  useEffect(() => {
    const staffId = searchParams.get('staff_id');
    if (staffId && !selectedStaff && staffData?.data) {
      const staff = staffData.data.find((s) => String(s.id) === staffId);
      if (staff) setSelectedStaff(setStaffSelectValue(staff));
    }

    const jurisdictionId = searchParams.get('jurisdiction_id');
    if (jurisdictionId && !selectedJurisdiction && jurisdictions) {
      const jurisdiction = jurisdictions.find((j) => String(j.id) === jurisdictionId);
      if (jurisdiction)
        setSelectedJurisdiction({ label: jurisdiction.name, value: String(jurisdiction.id) });
    }

    const providerId = searchParams.get('provider_id');
    if (providerId && !selectedProvider && providers?.data) {
      const provider = providers.data.find((p) => String(p.id) === providerId);
      if (provider)
        setSelectedProvider({ label: provider.external_name, value: String(provider.id) });
    }

    const typeValue = searchParams.get('insurance_type');
    if (typeValue && !selectedType && types?.data) {
      const type = types.data.find((t) => t.value === typeValue);
      if (type) setSelectedType({ label: type.label, value: type.value });
    }

    const scopeValue = searchParams.get('program_scope');
    if (scopeValue && !selectedProgramScope) {
      const scope = PROGRAM_SCOPES.find((s) => s.value === scopeValue);
      if (scope) setSelectedProgramScope(scope);
    }
  }, [
    searchParams,
    staffData,
    jurisdictions,
    providers,
    types,
    selectedStaff,
    selectedJurisdiction,
    selectedProvider,
    selectedType,
    selectedProgramScope
  ]);

  const updateSearchParam = (key: string, value: string | undefined) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
      return newParams;
    });
  };

  const { data: enrollmentData, isLoading: isEnrollmentLoading } = useStaffEnrollment(
    selectedStaff?.value.toString(),
    undefined,
    false,
    !!(selectedStaff && selectedJurisdiction),
    {
      jurisdiction: selectedJurisdiction?.label,
      insuranceProvider: selectedProvider?.label,
      insuranceType: selectedType?.label || '',
      programScope: selectedProgramScope?.value,
      onlyActive
    }
  );

  const { data: listData, isLoading: isListLoading } = useStaffEnrollments(
    {
      jurisdiction: selectedJurisdiction?.label,
      insurance_provider: selectedProvider?.label,
      insurance_type: selectedType?.label || '',
      program_scope: selectedProgramScope?.value,
      only_active: onlyActive,
      staff_id: selectedStaff?.value.toString()
    },
    !!selectedJurisdiction
  );

  const requiredSpecificity = useMemo(() => {
    // ALL fields need to be present
    return (
      selectedJurisdiction &&
      selectedProvider &&
      selectedType &&
      selectedProgramScope &&
      selectedStaff
    );
  }, [selectedJurisdiction, selectedProvider, selectedType, selectedProgramScope, selectedStaff]);

  return (
    <>
      <Container type="page">
        <div className="flex justify-between items-center mb-6">
          <PageHeader headingText="Credentialing" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <StaffSelect
            label="Staff Member"
            selectedOption={selectedStaff || { label: '', value: '' }}
            onChange={(val) => {
              const option = val as OptionType<string> | null;
              setSelectedStaff(option);
              updateSearchParam('staff_id', option?.value);
            }}
            filter={{}}
          />

          <AutocompleteSingleSelect
            label="Jurisdiction"
            options={jurisdictions?.map((j) => ({ label: j.name, value: String(j.id) })) || []}
            selectedOption={selectedJurisdiction || { label: '', value: '' }}
            onChange={(val) => {
              setSelectedJurisdiction(val);
              updateSearchParam('jurisdiction_id', val?.value);
            }}
            placeholder="Select Jurisdiction"
          />

          <AutocompleteSingleSelect
            label="Insurance Provider"
            options={providers?.data?.map((p) => ({ label: p.external_name, value: p.id })) || []}
            selectedOption={selectedProvider || { label: '', value: '' }}
            onChange={(val) => {
              setSelectedProvider(val);
              updateSearchParam('provider_id', val?.value);
            }}
            placeholder="Select Provider"
          />

          <AutocompleteSingleSelect
            label="Insurance Type"
            options={types?.data?.map((t) => ({ label: t.label, value: t.value })) || []}
            selectedOption={selectedType || { label: '', value: '' }}
            onChange={(val) => {
              setSelectedType(val);
              updateSearchParam('insurance_type', val?.value);
            }}
            placeholder="Select Type"
          />

          <AutocompleteSingleSelect
            label="Program Scope"
            options={PROGRAM_SCOPES}
            selectedOption={selectedProgramScope || { label: '', value: '' }}
            onChange={(val) => {
              setSelectedProgramScope(val);
              updateSearchParam('program_scope', val?.value);
            }}
            placeholder="Select Scope"
          />

          <Checkbox
            id="onlyActive"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
            label="Only Active"
          />
        </div>

        {requiredSpecificity && selectedStaff && (
          <div className="mb-8">
            {isEnrollmentLoading ? (
              <div className="text-center py-8 text-gray-500">Loading enrollment data...</div>
            ) : enrollmentData?.staff_id ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <span className="text-2xl">✅</span>
                <div>
                  <h3 className="font-bold text-green-800">Staff Member Enrolled</h3>
                  <p className="text-green-700 text-sm">
                    {selectedStaff.label} is enrolled in {selectedJurisdiction?.label}
                    {selectedProvider ? ` for ${selectedProvider.label}` : ''}
                    {selectedType ? ` (${selectedType.label})` : ''}
                    {selectedProgramScope ? ` [${selectedProgramScope.label}]` : ''}.
                  </p>
                </div>
              </div>
            ) : (
              <StaffEnrollmentWarning
                staffId={selectedStaff.value.toString()}
                staffName={selectedStaff.label}
                jurisdiction={selectedJurisdiction?.label}
                insurance={selectedProvider?.label}
                insuranceType={selectedType?.label}
                programScope={selectedProgramScope?.value}
              />
            )}
          </div>
        )}
      </Container>
      <Container type="full-width-page">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {isListLoading ? (
            <div className="text-center py-8 text-gray-500">Loading list data...</div>
          ) : listData && listData.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Staff Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Jurisdiction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Verifiable Payer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Insurance Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Program Scope
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Effective
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Terminated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {listData.map((enrollment) => (
                  <tr
                    key={`${enrollment.staff_id}-${enrollment.jurisdiction_id}-${enrollment.effective_on}-${enrollment.terminated_on}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center gap-2">
                      {enrollment.staff_name || enrollment.staff_id}
                      <button
                        onClick={() => {
                          const staffOption = {
                            label: enrollment.staff_name || enrollment.staff_id,
                            value: enrollment.staff_id
                          };
                          setSelectedStaff(staffOption);
                          updateSearchParam('staff_id', enrollment.staff_id);
                        }}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                        title="Filter by this staff member">
                        <FontAwesomeIcon icon={faFilter} size="xs" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {enrollment.jurisdiction_name || enrollment.jurisdiction_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {enrollment.verifiable_payer_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatSeperatedString(enrollment.line_of_business) || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatSeperatedString(enrollment.program_scopes, ';') || '-'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {enrollment.effective_on}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {enrollment.terminated_on || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {enrollment.enrollment_status || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {enrollment.verifiable_provider_enrollment_request_id ? (
                        <a
                          href={`https://affecttherapeutics.lightning.force.com/lightning/r/verifiable__ProviderEnrollment__c/${enrollment.verifiable_provider_enrollment_request_id}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline">
                          View in Verifiable
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {selectedJurisdiction
                ? 'No enrollments found for the selected criteria.'
                : 'Select a jurisdiction to view enrollments.'}
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export default Credentialing;
